import { defineStore } from 'pinia';

import { useLocationStorage, useOrganizationStorage } from '@/shared/config';

import type {
  AuthContextLoadResult,
  Employee,
  ImpersonationState,
  Location,
  Organization,
  OrganizationModule,
  User,
} from './types';

import { fetchAuthContext } from '../api/auth-api';
import { getAccessibleLocations } from '../lib/location-access';

const createEmptyImpersonationState = (): ImpersonationState => ({
  active: false,
  session_id: null,
  started_at: null,
  impersonator: null,
  target_user: null,
  organization: null,
});

interface AuthState {
  user: User | null;
  currentEmployee: Employee | null;
  currentOrganization: Organization | null;
  currentLocation: Location | null;
  permissions: string[];
  availableOrganizations: Organization[];
  impersonation: ImpersonationState;
  isContextChecked: boolean;
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: null,
    currentEmployee: null,
    currentOrganization: null,
    currentLocation: null,
    permissions: [],
    availableOrganizations: [],
    impersonation: createEmptyImpersonationState(),
    isContextChecked: false,
  }),

  getters: {
    isAuthenticated: (state): boolean => state.user !== null,
    isImpersonating: (state): boolean => state.impersonation.active,

    hasPermission:
      (state) =>
      (permission: string): boolean => {
        return state.permissions.includes(permission);
      },

    hasModule:
      (state) =>
      (module: OrganizationModule): boolean => {
        return state.currentOrganization?.enabled_modules?.includes(module) ?? true;
      },

    hasAnyPermission:
      (state) =>
      (permissions: string[]): boolean => {
        return permissions.some((p) => state.permissions.includes(p));
      },

    hasAllPermissions:
      (state) =>
      (permissions: string[]): boolean => {
        return permissions.every((p) => state.permissions.includes(p));
      },

    availableLocations: (state): Location[] => {
      const currentOrg = state.availableOrganizations.find((org) => org.id === state.currentOrganization?.id);

      return getAccessibleLocations(
        currentOrg ? [currentOrg] : [],
        state.currentEmployee,
        ({ location, organizationId }) => {
          console.warn('[FIX:location-switch] Дубль локации удалён из верхнего свитчера', {
            locationId: location.id,
            organizationId,
          });
        },
      );
    },
  },

  actions: {
    applyOrganizationSettings({
      id,
      title,
      timezone,
      resource_term: resourceTerm,
    }: Pick<Organization, 'id' | 'title' | 'timezone' | 'resource_term'>): void {
      for (const organization of [this.currentOrganization, ...this.availableOrganizations]) {
        if (organization?.id === id) {
          organization.title = title;
          organization.timezone = timezone;
          organization.resource_term = resourceTerm;
        }
      }
    },

    async loadContext(): Promise<AuthContextLoadResult> {
      const result = await fetchAuthContext();

      if (result.status === 'failure') {
        return result;
      }

      if (result.status === 'unauthenticated') {
        this.clearContext();
        this.isContextChecked = true;

        return result;
      }

      const data = result.context;
      this.user = data.user;
      this.currentEmployee = data.employee;
      this.currentOrganization = data.organization;
      this.currentLocation = data.location || null;
      this.permissions = data.permissions;
      this.availableOrganizations = data.available_organizations;
      this.impersonation = data.impersonation;
      this.isContextChecked = true;

      // Если организация не найдена, не оставляем устаревший tenant context.
      const organizationStorage = useOrganizationStorage();

      if (!data.organization && organizationStorage.getOrganizationId()) {
        organizationStorage.resetOrganizationId();
      }

      const { clearLocationId, getLocationId } = useLocationStorage();

      if (!data.location && getLocationId()) {
        clearLocationId();
        console.warn('[auth-store.loadContext] Устаревший last-location-id удалён', {
          organizationId: data.organization?.id ?? null,
        });
      }

      return result;
    },

    setUser(user: User) {
      this.user = user;
      // Сбрасываем флаг проверки чтобы загрузить полный контекст при следующей навигации
      this.isContextChecked = false;
    },

    setOrganization(organization: Organization) {
      this.currentOrganization = organization;
      useOrganizationStorage().setOrganizationId(organization.id);
      // Перезагружаем контекст для новой организации
      void this.loadContext();
    },

    setLocation(location: Location) {
      this.currentLocation = location;
    },

    clearLocation() {
      this.currentLocation = null;
    },

    clearContext() {
      this.user = null;
      this.currentEmployee = null;
      this.currentOrganization = null;
      this.currentLocation = null;
      this.permissions = [];
      this.availableOrganizations = [];
      this.impersonation = createEmptyImpersonationState();
      this.isContextChecked = false;
      useOrganizationStorage().resetOrganizationId();
      useLocationStorage().clearLocationId();
    },
  },
});
